import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserIdFromToken, getUserIdFromStorage } from "./utils/auth";
import './assets/main.css';
import styles from './SessionHistory.module.css';

/* ---------------- UI Primitives ---------------- */

const Card = ({ children, className = "" }) => (
  <div className={`${styles.card} ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false }) => {
  const variantClass = variant === "primary" 
    ? styles.buttonPrimary 
    : variant === "danger" 
    ? styles.buttonDanger 
    : styles.buttonSecondary;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${styles.button} ${variantClass} ${className}`}
    >
      {children}
    </button>
  );
};

/* ---------------- Edit Modal ---------------- */

const EditSessionModal = ({ session, onClose, onSave }) => {
  const [packsOpened, setPacksOpened] = useState(session.packsOpened);
  const [datePulled, setDatePulled] = useState(
    session.datePulled ? new Date(session.datePulled).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [cardsByRarity, setCardsByRarity] = useState([...session.cardsByRarity]);
  const [selectedGroup, setSelectedGroup] = useState(session.group?._id || "");
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    const loadGroups = async () => {
      let userId = getUserIdFromToken();
      if (!userId) {
        userId = getUserIdFromStorage();
      }

      if (userId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/groups?userId=${userId}`);
          const data = await response.json();
          
          // Sort groups with Default first
          const sortedGroups = data.sort((a, b) => {
            if (a.name === 'Default') return -1;
            if (b.name === 'Default') return 1;
            return a.name.localeCompare(b.name);
          });
          setGroups(sortedGroups);
          
          // If session has no group, set it to Default group
          if (!session.group?._id && sortedGroups.length > 0) {
            const defaultGroup = sortedGroups.find(g => g.name === 'Default');
            if (defaultGroup) {
              setSelectedGroup(defaultGroup._id);
            } else {
              setSelectedGroup(sortedGroups[0]._id);
            }
          }
        } catch (error) {
          console.error('Error loading groups:', error);
        }
      }
    };

    loadGroups();
  }, []);

  const handleSave = async () => {
    await onSave(session._id, { 
      packsOpened, 
      datePulled, 
      cardsByRarity,
      group: selectedGroup
    });
    onClose();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Edit Session</h2>
          <button onClick={onClose} className={styles.modalCloseButton}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Session Info */}
          <div className={styles.sessionInfo}>
            <p className={styles.sessionInfoRow}>Set: <span className={styles.sessionInfoValue}>{session.set?.name}</span></p>
            <p className={styles.sessionInfoRow}>Created: <span className={styles.sessionInfoValue}>{new Date(session.createdAt).toLocaleDateString()}</span></p>
          </div>

          {/* Group Selection */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              Group
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className={styles.formInput}
            >
              {groups.map(group => (
                <option key={group._id} value={group._id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Packs Opened */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              Packs Opened
            </label>
            <input
              type="number"
              min={1}
              value={packsOpened}
              onChange={(e) => setPacksOpened(Number(e.target.value))}
              className={styles.formInput}
            />
          </div>

          {/* Date Pulled */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              Date Pulled
            </label>
            <input
              type="date"
              value={datePulled}
              onChange={(e) => setDatePulled(e.target.value)}
              className={styles.formInput}
            />
          </div>

          {/* Cards by Rarity */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              Cards by Rarity
            </label>
            <div className={styles.rarityInputsContainer}>
              {cardsByRarity.map((item, idx) => (
                <div key={item.rarity} className={styles.rarityInputRow}>
                  <span className={styles.rarityLabel}>{item.rarity}</span>
                  <input
                    type="number"
                    min={0}
                    value={item.count}
                    onChange={(e) => {
                      const newCards = [...cardsByRarity];
                      newCards[idx].count = Number(e.target.value);
                      setCardsByRarity(newCards);
                    }}
                    className={styles.rarityInput}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className={styles.modalActions}>
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- Main Page ---------------- */

export default function SessionHistory() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSession, setEditingSession] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    let userId = getUserIdFromToken();
    
    if (!userId) {
      userId = getUserIdFromStorage();
    }

    if (userId) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions?userId=${userId}`);
        const data = await response.json();
        setSessions(data);
      } catch (error) {
        console.error('Error loading sessions:', error);
      }
    }
    
    setLoading(false);
  };

  const deleteSession = async (sessionId) => {
    console.log('Delete button clicked for session:', sessionId);
    
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to delete this session? This action cannot be undone.'
    );
    
    console.log('User confirmed deletion:', confirmed);
    
    if (!confirmed) {
      return; // User cancelled, don't delete
    }

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}`, {
            method: 'DELETE'
        });
        if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to delete session: ${errorData.error || 'Unknown error'}`);
        return;
        }
        // Reload sessions after deletion
        await loadSessions();
        alert('Session deleted successfully!');
        } catch (error) {
            console.error('Error deleting session:', error);
            alert('Failed to delete session');
        }
    };
  const handleEditSession = async (sessionId, updates) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to update session: ${errorData.error || 'Unknown error'}`);
        return;
      }

      // Reload sessions after update
      await loadSessions();
      alert('Session updated successfully!');
    } catch (error) {
      console.error('Error updating session:', error);
      alert('Failed to update session');
    }
  };

  const getTotalCards = (cardsByRarity) => {
    return cardsByRarity.reduce((sum, item) => sum + item.count, 0);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <p className={styles.loadingText}>Loading sessions...</p>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1>Session History</h1>
            <p>View and edit your past opening sessions</p>
          </div>
          <Button onClick={() => navigate('/dashboard')} variant="secondary">
            Back to Dashboard
          </Button>
        </div>

        {/* Sessions List */}
        <Card>
          {sessions.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateText}>No sessions found</p>
              <Button 
                onClick={() => navigate('/sessions/new')} 
                className={styles.emptyStateButton}
              >
                Create Your First Session
              </Button>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr className={styles.tableHeaderRow}>
                    <th className={styles.tableHeaderCell}>Date Pulled</th>
                    <th className={styles.tableHeaderCell}>Set</th>
                    <th className={styles.tableHeaderCell}>Group</th>
                    <th className={styles.tableHeaderCell}>Packs</th>
                    <th className={styles.tableHeaderCell}>Cards</th>
                    <th className={styles.tableHeaderCell}>Rarities</th>
                    <th className={styles.tableHeaderCellRight}>Actions</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {sessions.map((session) => (
                    <tr key={session._id} className={styles.tableRow}>
                      <td className={styles.tableCell}>
                        {new Date(session.datePulled || session.createdAt).toLocaleDateString()}
                      </td>
                      <td className={styles.tableCellBold}>
                        {session.set?.name || 'Unknown Set'}
                      </td>
                      <td className={styles.tableCell}>
                        {session.group?.name || '-'}
                      </td>
                      <td className={styles.tableCell}>
                        {session.packsOpened}
                      </td>
                      <td className={styles.tableCell}>
                        {getTotalCards(session.cardsByRarity)}
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.rarityBadgesContainer}>
                          {session.cardsByRarity.map((item) => (
                            <span 
                              key={item.rarity}
                              className={styles.rarityBadge}
                            >
                              {item.rarity}: {item.count}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={styles.tableCellRight}>
                        <div className={styles.actionButtons}>
                          <Button
                            onClick={() => setEditingSession(session)}
                            variant="secondary"
                            className={styles.actionButton}
                          >
                            Edit
                          </Button>
                          
                          <Button
                            onClick={() => deleteSession(session._id)}
                            variant="secondary"
                            className={styles.actionButton}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Edit Modal */}
        {editingSession && (
          <EditSessionModal
            session={editingSession}
            onClose={() => setEditingSession(null)}
            onSave={handleEditSession}
          />
        )}
      </div>
    </div>
  );
}
